import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Box } from '@mui/material';
import { useTheme } from '@mui/material/styles';

import { AppBarWithActionButtons } from '../../../../components/designe/app-bars/app-bar-with-buttons/AppBarWithActionButtons';
import AppButton from '../../../../components/designe/buttons/app-button/AppButton';
import AppHelpTooltipButton from '../../../../components/designe/tooltips/AppHelpTooltipButton';
import ConfirmDialog from '../../../../components/reusable/confirm-dialog/ConfirmDialog';
import ViewField from '../../../../components/reusable/view-field/ViewField';
import { useNavigationStack } from '../../../../context/NavigationStackContext';
import { useUserConfig } from '../../../../context/UserConfigContext';
import {
  useGetMyDetails,
  usePostOnboardingReset,
} from '../../../../redux/api/users/usersApi';
import { TrainingSetItemStatusListMode } from '../../../../shared/constants';
import { Paths } from '../../../../shared/paths';
import { formatTimeValueByConfig } from '../../../../utils/datetime/date-time-utils';
import { useStyles } from './FunctionalityView.styles';

export default function FunctionalityView() {
  const { t } = useTranslation();
  const theme = useTheme();

  const { classes } = useStyles(theme);
  const { replace } = useNavigationStack();
  const { refetch: refetchMe } = useGetMyDetails();
  const { options } = useUserConfig();
  const [postOnboardingReset, { isLoading: isResettingOnboarding }] =
    usePostOnboardingReset();
  const [isResetDialogOpen, setIsResetDialogOpen] = useState(false);

  const statusMode = options.trainingSetItemStatusListMode;
  const restSec = options.defaultSetItemRestSec;

  const restDisplay = useMemo(
    () =>
      restSec === null
        ? t('common.notSet')
        : formatTimeValueByConfig(restSec, t, {
            showHours: false,
            showMinutes: true,
            showSeconds: true,
          }),
    [restSec, t],
  );

  return (
    <div className={classes.wrapper}>
      <Box className={classes.content}>
        <Box>
          <ViewField
            label={t('settings.showSaveSetButton')}
            tooltip={
              <AppHelpTooltipButton
                text={t('settings.functionality.showSaveSetButtonHint')}
              />
            }
            value={options.showSaveSetButton ? t('common.yes') : t('common.no')}
            noShadow
            className={classes.viewItem}
          />
        </Box>
        <Box>
          <ViewField
            label={t('settings.showUploadSetButton')}
            tooltip={
              <AppHelpTooltipButton
                text={t('settings.functionality.showUploadSetButtonHint')}
              />
            }
            value={
              options.showUploadSetButton ? t('common.yes') : t('common.no')
            }
            noShadow
            className={classes.viewItem}
          />
        </Box>
        <Box display="flex" gap={1} alignItems="center">
          <Box flex={1}>
            <ViewField
              label={t('settings.functionality.onboardingEnabled')}
              tooltip={
                <AppHelpTooltipButton
                  text={t('settings.functionality.onboardingEnabledHint')}
                />
              }
              value={
                options.onboardingEnabled ? t('common.yes') : t('common.no')
              }
              noShadow
              className={classes.viewItem}
            />
          </Box>
          <Box>
            <AppButton
              variant="outlined"
              color="primary"
              onClick={() => setIsResetDialogOpen(true)}>
              {t('settings.functionality.resetOnboardingProgress')}
            </AppButton>
          </Box>
        </Box>
        <Box>
          <ViewField
            label={t('settings.functionality.showTooltips')}
            tooltip={
              <AppHelpTooltipButton
                text={t('settings.functionality.showTooltipsHint')}
              />
            }
            value={options.showTooltips ? t('common.yes') : t('common.no')}
            noShadow
            className={classes.viewItem}
          />
        </Box>
        <Box>
          <ViewField
            label={t('settings.functionality.showExerciseUnits')}
            tooltip={
              <AppHelpTooltipButton
                text={t('settings.functionality.showExerciseUnitsHint')}
              />
            }
            value={options.showExerciseUnits ? t('common.yes') : t('common.no')}
            noShadow
            className={classes.viewItem}
          />
        </Box>
        <Box>
          <ViewField
            label={t('settings.functionality.trainingSetItemStatusListMode')}
            tooltip={
              <AppHelpTooltipButton
                text={t(
                  'settings.functionality.trainingSetItemStatusListModeHint',
                )}
              />
            }
            value={
              statusMode === TrainingSetItemStatusListMode.primary
                ? t(
                    'settings.functionality.trainingSetItemStatusListMode_primary',
                  )
                : t('settings.functionality.trainingSetItemStatusListMode_all')
            }
            noShadow
            className={classes.viewItem}
          />
        </Box>
        <Box>
          <ViewField
            label={t('settings.functionality.defaultSetItemRestSec')}
            tooltip={
              <AppHelpTooltipButton
                text={t('settings.functionality.defaultSetItemRestSecHint')}
              />
            }
            value={restDisplay}
            noShadow
            className={classes.viewItem}
          />
        </Box>
        <Box className={classes.buttonWrapper}>
          <AppBarWithActionButtons
            onEditClick={() => replace(Paths.settings.toFunctionalityEdit)}
          />
        </Box>
      </Box>
      <ConfirmDialog
        open={isResetDialogOpen}
        onClose={() => setIsResetDialogOpen(false)}
        title={t('settings.functionality.resetOnboardingProgressConfirmTitle')}
        onSubmit={() => {
          postOnboardingReset()
            .unwrap()
            .then(() => {
              setIsResetDialogOpen(false);
              refetchMe();
            });
        }}
        isLoading={isResettingOnboarding}>
        {t('settings.functionality.resetOnboardingProgressConfirmMessage')}
      </ConfirmDialog>
    </div>
  );
}
